import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import {
  MapContainer,
  TileLayer,
  useMap,
  Polygon,
  ImageOverlay,
  LayersControl,
  LayerGroup
} from 'react-leaflet';
import L from 'leaflet';
import { GET_LISTING_TILES } from 'graphql/tile';
import {
  setTreeSegmentationTiles,
  setUnsegmentedTiles,
  setCHMTiles,
  setTaskModalDialogVisible,
  setTreeSegmentationTilesTotalCount
} from 'reducers/viewListingReducer';
import { getIntersectingTiles } from 'helpers/gis';
import TaskModal from './TaskModalDialog';

const { BaseLayer, Overlay } = LayersControl;

function SetBounds({ bounds }) {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [bounds, map]);

  return null;
}

SetBounds.propTypes = {
  bounds: PropTypes.array.isRequired
};

// Define the thresholds and corresponding colors
const thresholds = [0, 2, 4, 8, 10, 12, 15, 25, 50, '+'];
const colors = [
  [255, 255, 204], // Light yellow
  [255, 237, 160], // Light orange-yellow
  [254, 217, 118], // Orange-yellow
  [254, 178, 76], // Light orange
  [253, 141, 60], // Orange
  [252, 78, 42], // Red-orange
  [227, 26, 28], // Red
  [189, 0, 38], // Dark red
  [128, 0, 38] // Darker red
];

// Convert RGB array to hex color string
const rgbToHex = rgb => {
  return `#${rgb.map(value => value.toString(16).padStart(2, '0')).join('')}`;
};

// Get color based on value
const getColor = d => {
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (d > thresholds[i]) {
      return rgbToHex(colors[i]);
    }
  }
  return '#000000'; // Fallback color
};

const Legend = () => {
  const map = useMap();

  useEffect(() => {
    const legend = L.control({ position: 'bottomright' });

    legend.onAdd = function () {
      const div = L.DomUtil.create(
        'div',
        'view-listing-map-info view-listing-map-legend'
      );
      const grades = thresholds;
      const labels = [];
      let from, to;

      for (let i = 0; i < grades.length - 1; i++) {
        from = grades[i];
        to = grades[i + 1];

        labels.push(
          `<i style="background:${getColor(from + 0.1)}"></i> ${from}${
            to ? `&ndash;${to}` : '+'
          }`
        );
      }

      div.innerHTML = labels.join('<br>');
      return div;
    };

    legend.addTo(map);

    return () => {
      legend.remove();
    };
  }, [map]);

  return null;
};

const MapPane = () => {
  const {
    id,
    bounds,
    landParcels,
    chmTiles,
    treeSegmentationTiles,
    treeSegmentationTilesTotalCount
  } = useSelector(state => state.viewListing);
  const dispatch = useDispatch();

  const [activeLayer, setActiveLayer] = useState(null);

  const {
    loading: loadingTreeSegmenetation,
    data: dataTreeSegmentation,
    fetchMore: fetchMoreTreeSegmentation,
    refetch: refetchTreeSegmentation
  } = useQuery(GET_LISTING_TILES, {
    variables: {
      layerType: 'TREE_SEGMENTATION',
      listingId: id,
      page: 0,
      limit: 100
    },
    skip: !id || activeLayer === 'Canopy Height Model',
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: loadingCHM,
    data: dataCHM,
    fetchMore: fetchMoreCHM
  } = useQuery(GET_LISTING_TILES, {
    variables: {
      layerType: 'CANOPY_HEIGHT_MODEL',
      listingId: id,
      page: 0,
      limit: 100
    },
    skip: !id || activeLayer === 'Tree Segmentation',
    notifyOnNetworkStatusChange: true
  });

  const handleLayerChange = layerName => {
    setActiveLayer(layerName);
  };

  useEffect(() => {
    if (loadingTreeSegmenetation || !dataTreeSegmentation) {
      return;
    }

    const { items, totalCount, hasNext } = dataTreeSegmentation.tiles;

    dispatch(
      setTreeSegmentationTiles(
        items.map(t => ({
          id: t.id,
          url: `${process.env.REACT_APP_API_URL}/tiles/tree_segmentation/${t.id}?listing_id=${id}`,
          bounds: [
            [t.extent.points[0].lng, t.extent.points[0].lat],
            [t.extent.points[2].lng, t.extent.points[2].lat]
          ],
          opacity: 0.8,
          zoom: t.zoom,
          x: t.x,
          y: t.y
        }))
      )
    );

    dispatch(setTreeSegmentationTilesTotalCount(totalCount));

    if (hasNext) {
      fetchMoreTreeSegmentation({
        variables: {
          page: dataTreeSegmentation.tiles.page + 1, // Calculate next page
          limit: 100
        }
      });
    }
  }, [loadingTreeSegmenetation, dataTreeSegmentation]);

  useEffect(() => {
    if (treeSegmentationTilesTotalCount !== treeSegmentationTiles.length) {
      return;
    }
    // finished loading all tree segmentation tiles, check if any tiles that have not been inference for segmentation
    // each tile is a tuple [zoom, x, y]
    const allTiles = getIntersectingTiles(
      landParcels.map(l => l.coordinates),
      16
    );
    const unsegmentedTiles = allTiles.filter(
      t1 =>
        !treeSegmentationTiles.find(
          t2 => t1[0] === t2.zoom && t1[1] === t2.x && t1[2] === t2.y
        )
    );
    if (unsegmentedTiles.length > 0) {
      dispatch(setTaskModalDialogVisible(true));
    }
    dispatch(setUnsegmentedTiles(unsegmentedTiles));
  }, [treeSegmentationTiles, treeSegmentationTilesTotalCount]);

  useEffect(() => {
    if (loadingCHM || !dataCHM) {
      return;
    }

    dispatch(
      setCHMTiles(
        dataCHM.tiles.items.map(t => ({
          id: t.id,
          url: `${process.env.REACT_APP_API_URL}/tiles/canopy_height_model/${t.id}?listing_id=${id}`,
          bounds: [
            [t.extent.points[0].lng, t.extent.points[0].lat],
            [t.extent.points[2].lng, t.extent.points[2].lat]
          ],
          opacity: 0.8
        }))
      )
    );
    if (dataCHM.tiles.hasNext) {
      fetchMoreCHM({
        variables: {
          page: dataCHM.tiles.page + 1, // Calculate next page
          limit: 100
        }
      });
    }
  }, [loadingCHM, dataCHM]);

  useEffect(() => {
    if (
      loadingTreeSegmenetation ||
      !dataTreeSegmentation ||
      !dataTreeSegmentation.tiles.hasNext
    )
      return;

    fetchMoreTreeSegmentation({
      variables: {
        page: dataTreeSegmentation.tiles.page + 1,
        limit: 100
      }
    });
  }, [dataTreeSegmentation, loadingTreeSegmenetation]);

  if (!bounds) {
    return <></>;
  }

  return (
    <>
      <MapContainer zoom={17} zoomSnap={0.5} radius={200} className="h-100">
        <SetBounds bounds={bounds.map(b => [b[1], b[0]])} />
        <LayersControl
          position="topright"
          change={e => handleLayerChange(e.name)}
        >
          <BaseLayer checked name="Base">
            <TileLayer
              url={process.env.REACT_APP_LEAFLET_MAPBOX_SATELLITE_STREETS_V12_URL.replace(
                '{access_token}',
                process.env.REACT_APP_LEAFLET_MAPBOX_ACCESS_TOKEN
              )}
            />
          </BaseLayer>
          <Overlay checked name="Land Parcels">
            <LayerGroup>
              {landParcels.map(parcel => {
                return (
                  <Polygon
                    key={parcel.id}
                    pathOptions={{ color: 'white' }}
                    positions={parcel.coordinates.map(c => [c[1], c[0]])}
                    weight={0.5}
                  ></Polygon>
                );
              })}
            </LayerGroup>
          </Overlay>
          <Overlay checked name="Tree Segmentation">
            <LayerGroup>
              {treeSegmentationTiles.map(img => (
                <ImageOverlay
                  key={img.id}
                  className="image-on-map"
                  interactive={true}
                  url={img.url}
                  bounds={img.bounds.map(b => [b[1], b[0]])}
                  opacity={img.opacity}
                />
              ))}
            </LayerGroup>
          </Overlay>
          <Overlay name="Canopy Height Model">
            <LayerGroup>
              {chmTiles.map(img => (
                <ImageOverlay
                  key={img.id}
                  className="image-on-map"
                  interactive={true}
                  url={img.url}
                  bounds={img.bounds}
                  opacity={img.opacity}
                />
              ))}
            </LayerGroup>
          </Overlay>
        </LayersControl>
        <Legend />
      </MapContainer>
      <TaskModal refetchTreeSegmentation={refetchTreeSegmentation} />
    </>
  );
};

export default MapPane;
